import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Users, CheckCircle, AlertCircle, Crown, Zap,
    MapPin, Briefcase, Clock, CreditCard, Shield, Star
} from 'lucide-react';
import {
    loadIamportScript,
    initializeIamport,
    requestUnifiedPayment,
    createServicePayment
} from '../services/UnifiedPaymentService';

function MatchingSubscription({ user }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [paymentProcessing, setPaymentProcessing] = useState(false);

    // 기공소 프로필 정보
    const [profileData, setProfileData] = useState({
        region: '',
        detailAddress: '',
        specialties: [],
        monthlyCapacity: '',
        introduction: '',
        minPrice: '',
        avgDeliveryDays: ''
    });

    const specialtyOptions = [
        '크라운/브릿지', '임플란트', '지르코니아', '올세라믹',
        '틀니', '교정장치', '라미네이트', '스플린트',
        '금속도재', '투명교정'
    ];

    const capacityOptions = [
        { value: 'under10', label: '월 10건 미만' },
        { value: '10to30', label: '월 10~30건' },
        { value: '30to50', label: '월 30~50건' },
        { value: 'over50', label: '월 50건 이상' }
    ];

    const plans = [
        {
            id: 'basic',
            name: '베이직',
            price: 30000,
            icon: <Zap size={24} />,
            color: '#3b82f6',
            features: [
                '매칭 풀에 노출',
                '치과 연결 요청 수신',
                '기본 프로필 표시',
                '월간 매칭 리포트'
            ]
        },
        {
            id: 'premium',
            name: '프리미엄',
            price: 50000,
            icon: <Crown size={24} />,
            color: '#f59e0b',
            popular: true,
            features: [
                '베이직 모든 혜택',
                '검색 결과 상위 노출',
                '프리미엄 배지 표시',
                '우선 매칭 알림',
                '상세 통계 제공'
            ]
        }
    ];

    useEffect(() => {
        if (user?.uid) {
            loadSubscription();
            loadIamportScript();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadSubscription = async () => {
        try {
            setLoading(true);
            const subDoc = await getDoc(doc(db, 'matchingLabs', user.uid));

            if (subDoc.exists()) {
                const data = subDoc.data();
                setSubscription(data);
                setProfileData({
                    region: data.region || '',
                    detailAddress: data.detailAddress || '',
                    specialties: data.specialties || [],
                    monthlyCapacity: data.monthlyCapacity || '',
                    introduction: data.introduction || '',
                    minPrice: data.minPrice || '',
                    avgDeliveryDays: data.avgDeliveryDays || ''
                });
            }
        } catch (error) {
            console.error('구독 정보 로드 실패:', error);
            showMessage('error', '정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const handleSpecialtyToggle = (specialty) => {
        setProfileData(prev => ({
            ...prev,
            specialties: prev.specialties.includes(specialty)
                ? prev.specialties.filter(s => s !== specialty)
                : [...prev.specialties, specialty]
        }));
    };

    const validateProfile = () => {
        if (!profileData.region) {
            showMessage('error', '지역을 입력해주세요.');
            return false;
        }
        if (profileData.specialties.length === 0) {
            showMessage('error', '전문 분야를 1개 이상 선택해주세요.');
            return false;
        }
        if (!profileData.monthlyCapacity) {
            showMessage('error', '월간 수용 가능 물량을 선택해주세요.');
            return false;
        }
        return true;
    };

    const handleSubscribe = async (plan) => {
        if (!validateProfile()) return;

        try {
            setPaymentProcessing(true);

            // 아임포트 초기화
            initializeIamport();

            // 결제 요청
            const paymentResult = await requestUnifiedPayment({
                serviceType: 'matching-' + plan.id,
                amount: plan.price,
                name: `매칭 서비스 ${plan.name} (30일)`,
                buyer_name: user.businessName || user.name || '',
                buyer_email: user.email || '',
                buyer_tel: user.phone || ''
            });

            if (paymentResult.success) {
                // 결제 성공 - 구독 정보 저장
                const expiresAt = new Date();
                expiresAt.setDate(expiresAt.getDate() + 30);

                await setDoc(doc(db, 'matchingLabs', user.uid), {
                    labId: user.uid,
                    labName: user.businessName || user.companyName || user.name || '',
                    email: user.email || '',
                    phone: user.phone || '',
                    plan: plan.id,
                    ...profileData,
                    isActive: true,
                    subscribedAt: serverTimestamp(),
                    expiresAt: expiresAt,
                    paymentId: paymentResult.imp_uid,
                    updatedAt: serverTimestamp()
                });

                // 결제 기록 저장
                await createServicePayment({
                    userId: user.uid,
                    serviceType: 'matching-' + plan.id,
                    amount: plan.price,
                    imp_uid: paymentResult.imp_uid,
                    merchant_uid: paymentResult.merchant_uid
                });

                setSubscription({
                    plan: plan.id,
                    isActive: true,
                    expiresAt: expiresAt
                });

                showMessage('success', `${plan.name} 플랜 등록이 완료되었습니다!`);
            } else {
                showMessage('error', paymentResult.error || '결제가 취소되었습니다.');
            }
        } catch (error) {
            console.error('구독 처리 실패:', error);
            showMessage('error', '구독 처리 중 오류가 발생했습니다.');
        } finally {
            setPaymentProcessing(false);
        }
    };

    const handleSaveProfile = async () => {
        if (!validateProfile()) return;

        try {
            setSaving(true);
            await updateDoc(doc(db, 'matchingLabs', user.uid), {
                ...profileData,
                updatedAt: serverTimestamp()
            });
            showMessage('success', '프로필이 저장되었습니다.');
        } catch (error) {
            console.error('프로필 저장 실패:', error);
            showMessage('error', '프로필 저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const isExpired = subscription?.expiresAt && new Date(subscription.expiresAt.toDate ? subscription.expiresAt.toDate() : subscription.expiresAt) < new Date();
    const isActive = subscription?.isActive && !isExpired;

    const getDaysRemaining = () => {
        if (!subscription?.expiresAt) return 0;
        const expiry = subscription.expiresAt.toDate ? subscription.expiresAt.toDate() : new Date(subscription.expiresAt);
        const diff = expiry - new Date();
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p>로딩 중...</p>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <div style={styles.headerIcon}>
                    <Users size={32} color="#6366f1" />
                </div>
                <div>
                    <h1 style={styles.title}>매칭 서비스</h1>
                    <p style={styles.subtitle}>치과와 연결되어 새로운 거래처를 확보하세요</p>
                </div>
            </div>

            {/* 메시지 */}
            {message.text && (
                <div style={{
                    ...styles.message,
                    ...(message.type === 'success' ? styles.messageSuccess : styles.messageError)
                }}>
                    {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                    <span>{message.text}</span>
                </div>
            )}

            {/* 현재 구독 상태 */}
            {isActive && (
                <div style={styles.currentPlan}>
                    <div style={styles.currentPlanHeader}>
                        <Shield size={24} color="#10b981" />
                        <span style={styles.currentPlanTitle}>현재 구독 중</span>
                    </div>
                    <div style={styles.currentPlanInfo}>
                        <div style={styles.planBadge}>
                            {subscription.plan === 'premium' ? <Crown size={16} /> : <Zap size={16} />}
                            <span>{subscription.plan === 'premium' ? '프리미엄' : '베이직'}</span>
                        </div>
                        <div style={styles.daysRemaining}>
                            <Clock size={16} />
                            <span>{getDaysRemaining()}일 남음</span>
                        </div>
                    </div>
                </div>
            )}

            {/* 프로필 설정 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                    <Briefcase size={20} />
                    매칭 프로필 설정
                </h2>
                <p style={styles.sectionDesc}>치과에게 보여질 정보를 입력해주세요</p>

                <div style={styles.formGrid}>
                    {/* 지역 */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            <MapPin size={16} /> 지역 *
                        </label>
                        <input
                            type="text"
                            value={profileData.region}
                            onChange={(e) => setProfileData(prev => ({ ...prev, region: e.target.value }))}
                            placeholder="예: 서울 강남구"
                            style={styles.input}
                        />
                    </div>

                    {/* 상세 주소 */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>상세 주소</label>
                        <input
                            type="text"
                            value={profileData.detailAddress}
                            onChange={(e) => setProfileData(prev => ({ ...prev, detailAddress: e.target.value }))}
                            placeholder="상세 주소 (선택)"
                            style={styles.input}
                        />
                    </div>

                    {/* 월간 물량 */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>월간 수용 가능 물량 *</label>
                        <select
                            value={profileData.monthlyCapacity}
                            onChange={(e) => setProfileData(prev => ({ ...prev, monthlyCapacity: e.target.value }))}
                            style={styles.select}
                        >
                            <option value="">선택해주세요</option>
                            {capacityOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* 평균 납기 */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>평균 납기일</label>
                        <input
                            type="text"
                            value={profileData.avgDeliveryDays}
                            onChange={(e) => setProfileData(prev => ({ ...prev, avgDeliveryDays: e.target.value }))}
                            placeholder="예: 3~5일"
                            style={styles.input}
                        />
                    </div>
                </div>

                {/* 전문 분야 */}
                <div style={styles.formGroup}>
                    <label style={styles.label}>전문 분야 * (복수 선택 가능)</label>
                    <div style={styles.specialtyGrid}>
                        {specialtyOptions.map(specialty => (
                            <button
                                key={specialty}
                                onClick={() => handleSpecialtyToggle(specialty)}
                                style={{
                                    ...styles.specialtyBtn,
                                    ...(profileData.specialties.includes(specialty) ? styles.specialtyBtnActive : {})
                                }}
                            >
                                {profileData.specialties.includes(specialty) && <CheckCircle size={14} />}
                                {specialty}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 소개 */}
                <div style={styles.formGroup}>
                    <label style={styles.label}>기공소 소개</label>
                    <textarea
                        value={profileData.introduction}
                        onChange={(e) => setProfileData(prev => ({ ...prev, introduction: e.target.value }))}
                        placeholder="치과에게 어필할 수 있는 기공소 소개를 작성해주세요"
                        style={styles.textarea}
                        rows={4}
                    />
                </div>

                {isActive && (
                    <button
                        onClick={handleSaveProfile}
                        disabled={saving}
                        style={styles.saveBtn}
                    >
                        {saving ? '저장 중...' : '프로필 저장'}
                    </button>
                )}
            </div>

            {/* 플랜 선택 */}
            {!isActive && (
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <CreditCard size={20} />
                        플랜 선택
                    </h2>
                    <p style={styles.sectionDesc}>치과 매칭 서비스에 등록하세요</p>

                    <div style={styles.planGrid}>
                        {plans.map(plan => (
                            <div
                                key={plan.id}
                                style={{
                                    ...styles.planCard,
                                    borderColor: plan.popular ? plan.color : '#e2e8f0'
                                }}
                            >
                                {plan.popular && (
                                    <div style={{ ...styles.popularBadge, backgroundColor: plan.color }}>
                                        <Star size={12} /> 인기
                                    </div>
                                )}
                                <div style={{ ...styles.planIcon, backgroundColor: `${plan.color}15`, color: plan.color }}>
                                    {plan.icon}
                                </div>
                                <h3 style={styles.planName}>{plan.name}</h3>
                                <div style={styles.planPrice}>
                                    <span style={styles.priceAmount}>{plan.price.toLocaleString()}</span>
                                    <span style={styles.priceUnit}>원/월</span>
                                </div>
                                <ul style={styles.featureList}>
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx} style={styles.featureItem}>
                                            <CheckCircle size={14} color={plan.color} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={() => handleSubscribe(plan)}
                                    disabled={paymentProcessing}
                                    style={{
                                        ...styles.subscribeBtn,
                                        backgroundColor: plan.color
                                    }}
                                >
                                    {paymentProcessing ? '처리 중...' : '등록하기'}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 안내 */}
            <div style={styles.infoBox}>
                <AlertCircle size={20} color="#6366f1" />
                <div>
                    <strong>매칭 서비스 안내</strong>
                    <p style={styles.infoText}>
                        • 등록 후 치과의 조건에 맞으면 자동으로 추천됩니다<br />
                        • 치과가 연결 요청을 보내면 알림을 받습니다<br />
                        • 연결을 수락하면 서로의 연락처가 공개됩니다
                    </p>
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: { minHeight: '100vh', backgroundColor: '#f8fafc', padding: '24px', maxWidth: '900px', margin: '0 auto' },
    loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' },
    spinner: { width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },

    header: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' },
    headerIcon: { width: '64px', height: '64px', backgroundColor: '#eef2ff', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#0f172a' },
    subtitle: { margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' },

    message: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', borderRadius: '12px', marginBottom: '24px', fontSize: '14px', fontWeight: '600' },
    messageSuccess: { backgroundColor: '#d1fae5', color: '#065f46' },
    messageError: { backgroundColor: '#fee2e2', color: '#991b1b' },

    currentPlan: { backgroundColor: '#ecfdf5', border: '2px solid #10b981', borderRadius: '16px', padding: '20px', marginBottom: '24px' },
    currentPlanHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' },
    currentPlanTitle: { fontSize: '16px', fontWeight: '700', color: '#065f46' },
    currentPlanInfo: { display: 'flex', alignItems: 'center', gap: '16px' },
    planBadge: { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#fff', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#0f172a' },
    daysRemaining: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#065f46' },

    section: { backgroundColor: '#fff', borderRadius: '16px', padding: '24px', marginBottom: '24px', border: '1px solid #e2e8f0' },
    sectionTitle: { display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' },
    sectionDesc: { margin: '0 0 20px 0', fontSize: '14px', color: '#64748b' },

    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '20px' },
    formGroup: { marginBottom: '16px' },
    label: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' },
    input: { width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    select: { width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    textarea: { width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' },

    specialtyGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    specialtyBtn: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', border: '2px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff', fontSize: '13px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
    specialtyBtnActive: { borderColor: '#6366f1', backgroundColor: '#eef2ff', color: '#6366f1' },

    saveBtn: { padding: '12px 24px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },

    planGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' },
    planCard: { position: 'relative', backgroundColor: '#fff', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', textAlign: 'center' },
    popularBadge: { position: 'absolute', top: '-10px', right: '20px', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', color: '#fff', fontSize: '11px', fontWeight: '700' },
    planIcon: { width: '56px', height: '56px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' },
    planName: { margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#0f172a' },
    planPrice: { marginBottom: '20px' },
    priceAmount: { fontSize: '32px', fontWeight: '800', color: '#0f172a' },
    priceUnit: { fontSize: '14px', color: '#64748b', marginLeft: '4px' },
    featureList: { listStyle: 'none', padding: 0, margin: '0 0 20px 0', textAlign: 'left' },
    featureItem: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', fontSize: '14px', color: '#475569', borderBottom: '1px solid #f1f5f9' },
    subscribeBtn: { width: '100%', padding: '14px', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },

    infoBox: { display: 'flex', gap: '16px', padding: '20px', backgroundColor: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe' },
    infoText: { margin: '8px 0 0 0', fontSize: '13px', color: '#1e40af', lineHeight: '1.6' }
};

export default MatchingSubscription;
